/**
 * Объект каталога на полу в плане: кровать, шкаф, торшер — вид сверху.
 *
 * Занимает прямоугольник «ширина × глубина» из каталога в натуральном масштабе,
 * повёрнутый вокруг центра. Начинка — SVG из каталога; здесь выделение и захват.
 * Квадратной подложки нет: символ залит белым только по своему контуру (`SymbolFrame`). Рамки вокруг предмета нет: его контур — это сам символ, а выделение —
 * синий цвет символа и тонкий штрих по габариту. Рисуется в локальных координатах объекта, поворот и
 * перенос делает вызывающий.
 */
import type { PointerEvent } from "react";
import { itemDimensions, type FloorItem } from "@/shared/plan";
import { isRoundItem, type CatalogItem } from "@/shared/catalog";
import { CatalogSymbol } from "../../CatalogSymbol";
import { planInk } from "../../planInk";

/** Габариты, если типа в каталоге больше нет — объект всё равно виден и удаляем. */
const MISSING = { width: 600, depth: 600, color: "#9aa3b2" };

interface Props {
	item: FloorItem;
	/** Тип из каталога; null — тип удалён из каталога */
	info: CatalogItem | null;
	selected: boolean;
	mmPerPx: number;
	interactive?: boolean;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const FurnitureItemShape = ({
	item,
	info,
	selected,
	mmPerPx,
	interactive = true,
	onPointerDown,
}: Props) => {
	const size = info ? itemDimensions(item, info) : MISSING;
	const w = size.width;
	const d = size.depth;
	const px = (n: number) => n * mmPerPx;
	const color = selected ? "#2f6feb" : planInk(info?.color ?? MISSING.color);
	// Круглость — из каталога: квадратный предмет (стиралка, кровать 1800×1800) остаётся прямоугольным
	const isRound = isRoundItem(info);

	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "move" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			<CatalogSymbol item={info} width={w} height={d} color={color} mmPerPx={mmPerPx} />
			{/* Выделение: символ уже синий, добавляем тонкий штрих по габариту */}
			{selected &&
				(isRound ? (
					<ellipse
						cx={0}
						cy={0}
						rx={w / 2}
						ry={d / 2}
						fill="none"
						stroke={color}
						strokeWidth={px(0.9)}
						strokeDasharray={`${px(4)} ${px(3)}`}
					/>
				) : (
					<rect
						x={-w / 2}
						y={-d / 2}
						width={w}
						height={d}
						fill="none"
						stroke={color}
						strokeWidth={px(0.9)}
						strokeDasharray={`${px(4)} ${px(3)}`}
					/>
				))}
			{/* Зона захвата поверх символа */}
			{isRound ? (
				<ellipse
					data-role="item"
					data-id={item.id}
					cx={0}
					cy={0}
					rx={w / 2}
					ry={d / 2}
					fill="transparent"
				/>
			) : (
				<rect
					data-role="item"
					data-id={item.id}
					x={-w / 2}
					y={-d / 2}
					width={w}
					height={d}
					fill="transparent"
				/>
			)}
		</g>
	);
};
